export const Collections = {
  tickets: 'tickets',
  ticketTypes: 'ticketTypes',
  mails: 'mails',
  mailTemplates: 'mail_templates',
  content: 'content',
  orders: 'orders',
  payments: 'payments',
  appData: 'app',
  bus: 'bus',
  busRoutes: 'busRoutes',
  busOrders: 'busOrders',
  festivalEditions: 'festivalEditions',
  events: 'events',
  eventNewsletterSends: 'eventNewsletterSends',
  communityMembers: 'communityMembers',
  emailCampaigns: 'emailCampaigns',
  guestlistEntries: 'guestlistEntries',
  doorEntries: 'doorEntries',
  departures: 'departures',
};

export type SaleState = 'draft' | 'scheduled' | 'open' | 'closed' | 'soldOut';

export type TicketingOwnerRef =
  | { kind: 'festivalEdition'; festivalEditionId: string }
  | { kind: 'event'; eventId: string };

/**
 * A callable-only, owner-scoped admission record. Guestlist entries are
 * deliberately independent from ticketing, orders, payments, community,
 * campaigns, mail, capacity, QR, and door-sale records.
 */
export type GuestlistEntry = {
  guestlistEntryId: string;
  owner: TicketingOwnerRef;
  name: string;
  /** Canonical lower-case email, or null for a name-only guest. */
  email: string | null;
  checkinAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdByUid: string;
  createdByEmail: string;
  updatedByUid: string;
  updatedByEmail: string;
};

export const WUEST_AFFAIRS_TWO_PARTY_MIGRATION_ID = 'wuest-affairs-two-party-v1' as const;

export type HistoricImportProvenance = {
  migrationId: string;
  sourceProject: 'wuest-affairs';
  sourceId: string;
  sourceRoot: string;
  sourceCollection: string;
  sourceDocumentId: string;
  sourceFingerprint: string;
  sourcePaymentId?: string;
};

export type BookingField = {
  fieldId: string;
  key: string;
  label: string;
  helpText?: string;
  required: boolean;
  appliesTo: 'order' | 'attendee';
  type: 'text' | 'textarea';
  sortOrder: number;
  archivedAt?: string | null;
};

export type FestivalEdition = {
  festivalEditionId: string;
  year: number;
  title: string;
  slug: string;
  startsAt: string;
  endsAt: string;
  location?: string;
  description?: string;
  activeForFestivalRoutes: boolean;
  /**
   * Optional soft-gate token for the private festival. When set, the public
   * festival/bus ticket UI is only shown to visitors whose stored token
   * matches. Empty/unset = festival ticketing is public.
   */
  accessToken?: string;
  festivalSaleState: SaleState;
  busSaleState: 'disabled' | SaleState;
  bookingFields: BookingField[];
  settings: {
    ticketsAvailable?: number;
    serviceFeePercent?: number;
    orderConfirmationTemplate?: string;
    ticketPaymentTemplate?: string;
    busOrderConfirmationTemplate?: string;
  };
  createdAt: string;
  updatedAt: string;
};

export type EventExternalLink = {
  label: string;
  url: string;
};

/**
 * Per-event microsite theme. Each value is a CSS color string. The public
 * event layout maps these onto its `--ev-*` CSS variables (with brand-palette
 * fallbacks), so a single event can recolor its whole self-contained world.
 */
export type WuwEventTheme = {
  /** Primary accent — buttons, headlines, highlights. Maps to --ev-accent. */
  primary?: string;
  /** Page background. Maps to --ev-bg (surface is derived from it). */
  background?: string;
  /** Secondary accent. Maps to --ev-accent2. */
  accent?: string;
  /** Body text / foreground color. Maps to --ev-ink. */
  foreground?: string;
};

export type WuwEvent = {
  eventId: string;
  title: string;
  slug: string;
  startsAt: string;
  endsAt?: string;
  startTimeLabel?: string;
  endTimeLabel?: string;
  locationName?: string;
  locationAddress?: string;
  imageUrl?: string;
  posterUrl?: string;
  description: string;
  externalLinks: EventExternalLink[];
  saleState: SaleState;
  abendkasseEnabled: boolean;
  abendkassePrice?: number | null; // single per-event Abendkasse price (€)
  /** Optional per-event color identity for the public microsite. */
  theme?: WuwEventTheme;
  bookingFields?: BookingField[];
  importProvenance?: HistoricImportProvenance;
  settings?: {
    serviceFeePercent?: number;
    orderConfirmationTemplate?: string;
    ticketPaymentTemplate?: string;
  };
  createdAt: string;
  updatedAt: string;
};

export type Event = WuwEvent;

export type EventNewsletterSendRecipientSnapshot = {
  email: string;
  displayName: string | null;
  ticketIds: string[];
  orderIds: string[];
  mailId?: string;
  error?: string;
};

export type EventNewsletterSend = {
  sendId: string;
  eventId: string;
  status: 'creating' | 'queued' | 'failed';
  createdAt: string;
  updatedAt: string;
  createdByUid: string;
  createdByEmail?: string | null;
  subject: string;
  bodyText: string;
  bodyHtml: string;
  ctaLabel?: string;
  ctaUrl?: string;
  eventTitle: string;
  eventDateTime: string;
  recipientCount: number;
  createdMailCount: number;
  skippedMailCount: number;
  failedMailCount: number;
  mailIds: string[];
  recipients: EventNewsletterSendRecipientSnapshot[];
  errorMessage?: string;
};

/**
 * Abendkasse (door) sale — stored at `events/{eventId}/doorEntries/{doorEntryId}`.
 * One doc per "+ Add door guest" click. `pricePaid` snapshots the event's
 * `abendkassePrice` at click time so revenue is stable if the price later changes.
 */
export type DoorEntry = {
  doorEntryId: string;
  quantity: number; // always 1 from the one-click button
  pricePaid: number; // € charged at the door
  note?: string;
  createdAt: string; // ISO
  createdByEmail?: string | null;
  voidedAt?: string | null;
  voidedByEmail?: string | null;
};

/**
 * Anonymous departure — stored at `events/{eventId}/departures/{departureId}`.
 * One doc per "− Guest left" click. Used only for the occupancy estimate.
 */
export type Departure = {
  departureId: string;
  createdAt: string; // ISO
  createdByEmail?: string | null;
};

export type Order = {
  orderId: string;
  email: string;
  ticketIds: string[];
  /** Ticket subtotal in EUR (sum of ticket prices, before the service fee). */
  price: number;
  /**
   * Service fee snapshot, stored at order time so later config changes never
   * alter the cost of an existing order. Optional for legacy orders placed
   * before this was captured.
   */
  serviceFeePercent?: number;
  /** Service fee amount in EUR (price * serviceFeePercent / 100). */
  serviceFee?: number;
  /** Complete order cost in EUR (price + serviceFee). */
  total?: number;
  createdAt: string;
  knowsFromOrga: string;
  heardAboutUs: string;
  paidAt: string | null;
  paymentId: string | null;
  canceledAt?: string | null;
  owner?: TicketingOwnerRef;
  customFields?: Record<string, string>;
  importProvenance?: HistoricImportProvenance;
};

export type CommunityMemberOwnerRef = TicketingOwnerRef | { kind: 'legacy' };

export type CommunityMemberActivitySummary = {
  orderIds: string[];
  ticketIds: string[];
  busOrderIds: string[];
  busTicketIds: string[];
  firstAt: string | null;
  lastAt: string | null;
};

export type CommunityMemberOwnerActivity = {
  owner: CommunityMemberOwnerRef;
  purchased: CommunityMemberActivitySummary;
  paid: CommunityMemberActivitySummary;
  attended: CommunityMemberActivitySummary;
};

export type CommunityMemberCustomField = {
  key: string;
  value: string | number | boolean;
  sourceType: 'order' | 'ticket' | 'busOrder' | 'busTicket';
  sourceId: string;
};

export type NewsletterConsent = 'existingCustomerEventInterest' | 'explicitOptIn';

export type NewsletterSubscriptionSource =
  | 'historicalBackfill'
  | 'ticketOrder'
  | 'busOrder'
  | 'unsubscribeLink'
  | 'manual';

/**
 * Kept optional while existing private projections are initialized in a
 * guarded migration. Readers must fail closed until every field is present.
 */
export type CommunityMemberSubscriptionFields = {
  newsletterSubscribed?: boolean;
  newsletterConsent?: NewsletterConsent;
  subscriptionSource?: NewsletterSubscriptionSource;
  subscriptionUpdatedAt?: string;
  unsubscribedAt?: string | null;
  unsubscribeTokenVersion?: number;
};

/**
 * Private, additive projection over ticketing source records. Source tickets,
 * orders, and bus orders remain authoritative and must never be changed from
 * this collection.
 */
export type CommunityMember = CommunityMemberSubscriptionFields & {
  communityMemberId: string;
  normalizedEmail: string;
  /** Deterministic trimmed source email preserved for admin display. */
  email: string;
  names: string[];
  ticketIds: string[];
  orderIds: string[];
  busTicketIds: string[];
  busOrderIds: string[];
  eventIds: string[];
  festivalEditionIds: string[];
  ownerActivity: CommunityMemberOwnerActivity[];
  customFields: CommunityMemberCustomField[];
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type EmailCampaignStatus = 'draft' | 'ready' | 'sending' | 'sent' | 'canceled';

export type EmailCampaignTarget =
  | { kind: 'allCommunityMembers' }
  | { kind: 'owners'; owners: TicketingOwnerRef[] };

export type CampaignEmailContent = {
  templateName: 'campaign_email';
  headerTitle: string;
  headerSubtitle?: string;
  bodyText: string;
  /** Server-rendered, escaped plain-text snapshot for template and preview use. */
  bodyHtml?: string;
  ctaLabel?: string;
  ctaUrl?: string;
};

export type EmailCampaignContent = CampaignEmailContent;

export type CampaignActor = {
  uid: string;
  email: string | null;
};

export type CampaignAudienceCounts = {
  selectedSourceCount: number;
  matchingCommunityMemberCount: number;
  sourceTicketCount: number;
  uniqueRecipientCount: number;
  eligibleRecipientCount: number;
  suppressedUnsubscribedCount: number;
  suppressedNoConsentCount: number;
  excludedMissingOrInvalidEmailCount: number;
};

export type CampaignAudienceSnapshotSummary = CampaignAudienceCounts & {
  snapshotId: string;
  targetRevision: number;
  targetFingerprint: string;
  resolvedAt: string;
  resolverVersion: 'community-members-v1';
};

export type EmailCampaign = {
  campaignId: string;
  title: string;
  subject: string;
  status: EmailCampaignStatus;
  target: EmailCampaignTarget;
  targetRevision: number;
  targetFingerprint: string;
  content: EmailCampaignContent;
  createdBy: CampaignActor;
  createdAt: string;
  updatedAt: string;
  latestAudienceSnapshot?: CampaignAudienceSnapshotSummary;
  latestSendSummary?: CampaignSendSummary;
};

export type CampaignAudienceSnapshot = CampaignAudienceSnapshotSummary & {
  campaignId: string;
  kind: 'preview';
  target: EmailCampaignTarget;
  resolvedBy: CampaignActor;
};

export type CampaignDeliveryState = 'pending' | 'queued' | 'suppressed' | 'failed';

export type CampaignSendStatus = 'snapshotting' | 'queueing' | 'queued' | 'failed';

/** Aggregate-only status safe for the admin callable response and campaign root. */
export type CampaignSendSummary = {
  sendId: string;
  status: CampaignSendStatus;
  expectedRecipientCount: number;
  queuedCount: number;
  skippedCount: number;
  failedCount: number;
  pendingCount: number;
  updatedAt: string;
};

/** Immutable pre-queue snapshot held under emailCampaigns/{id}/sendAttempts. */
export type CampaignSendAttempt = CampaignSendSummary & {
  campaignId: string;
  kind: 'campaignFinal';
  createdAt: string;
  createdBy: CampaignActor;
  frozenTarget: EmailCampaignTarget;
  frozenTargetRevision: number;
  frozenTargetFingerprint: string;
  frozenSubject: string;
  frozenContent: EmailCampaignContent;
  audienceCounts: CampaignAudienceCounts;
  recipientDigest: string;
  snapshotComplete: boolean;
  /** A short server-owned lease prevents concurrent orchestration while allowing recovery. */
  operationLeaseExpiresAt?: string;
  snapshotCompletedAt?: string;
  failureReason?: 'audience_digest_mismatch' | 'queue_incomplete' | 'snapshot_incomplete';
};

/** Immutable recipient snapshot; queue state may only move forward. */
export type CampaignDelivery = {
  deliveryId: string;
  mailId: string;
  campaignId: string;
  sendId: string;
  communityMemberId: string;
  recipientEmail: string;
  unsubscribeTokenVersion: number;
  state: CampaignDeliveryState;
  createdAt: string;
  updatedAt: string;
  suppressedReason?: 'ineligible_at_queue_time';
  failureReason?: 'queue_incomplete';
};

export type Bus = {
  busId: string;
  type: 'arrival' | 'departure';
  departureTime: string;
  price: number;
  seatsTotal: number;
  seatsAvailable: number;
};

export type BusRoute = Bus & {
  busRouteId: string;
  owner: { kind: 'festivalEdition'; festivalEditionId: string };
  label?: string;
  saleState?: 'active' | 'hidden' | 'soldOut';
  sortOrder?: number;
  stripeProductId?: string;
  archivedAt?: string | null;
};

export type BusOrder = {
  orderId: string;
  email: string;
  tickets: {
    busTicketId: string;
    busId: string;
    name: string;
    email: string;
    checkinAt?: string | null;
    customFields?: Record<string, string>;
  }[];
  price: number;
  createdAt: string;
  paidAt: string | null;
  paymentId: string | null;
  canceledAt?: string | null;
  owner?: TicketingOwnerRef;
  customFields?: Record<string, string>;
};

export type Payment = {
  paymentId: string;
  orderId: string;
  createdAt: string;
  stripe: any;
  owner?: TicketingOwnerRef;
};

export type Ticket = {
  ticketId: string;
  orderId: string;
  name: string;
  email: string;
  supportsWith: string;
  ticketType: TicketType;
  price: number;
  createdAt: string;
  canceledAt?: string | null;
  checkinAt?: string | null;
  owner?: TicketingOwnerRef;
  customFields?: Record<string, string>;
  importProvenance?: HistoricImportProvenance;
  ownerHistory?: {
    name: string;
    email: string;
    changedAt: string;
  }[];
};

export type TicketType = {
  ticketTypeId?: string;
  owner?: TicketingOwnerRef;
  name: 'normal' | 'soli' | 'parking' | string;
  isPerson: boolean;
  label: string;
  description?: string;
  price: number;
  capacity?: number | null;
  available?: number | null;
  saleState?: 'active' | 'hidden' | 'soldOut';
  sortOrder?: number;
  stripeProductId?: string;
  archivedAt?: string | null;
};
